/**
 * Tracks microphone loudness against an adaptive background-noise estimate so a
 * VAD event can be judged as near-field speech (the user, holding the device)
 * rather than ambient chatter across a room.
 *
 * Thresholds are relative to the measured noise floor, not absolute, so the same
 * settings work in a silent room and in a cafe. Levels are RMS in 0..1, sampled
 * from the voice client's existing metering loop.
 */

export const MIC_ACTIVITY_DEFAULTS = {
  /** EMA weight while the floor follows a quieter sample (tracks down quickly). */
  floorAttack: 0.08,
  /**
   * EMA weight while the floor rises. Slow enough that speech onset cannot lift it,
   * fast enough to learn a room's ambient level within about a second of listening.
   */
  floorRelease: 0.02,
  /** A sample must exceed the floor by this factor to count as near-field. */
  marginRatio: 2.2,
  /** ...and by this absolute amount, so a near-zero floor still needs real signal. */
  marginAbsolute: 0.02,
  /** Hard minimum RMS — below this nothing counts as near-field speech. */
  minLevel: 0.03,
  /** Loud audio must persist this long before near-field speech is confirmed. */
  sustainMs: 160,
  /** Brief dips within a word do not break the sustain streak. */
  quietToleranceMs: 90,
  /** Confirmation stays valid this long after the last loud sample. */
  holdMs: 800,
  /** Cap per-sample time accounting so a stalled render loop cannot inflate totals. */
  maxSampleGapMs: 120,
  /** Metering is considered unusable if no sample arrived within this window. */
  freshSampleMs: 600,
};

export default class VoiceMicActivity {
  constructor(options = {}) {
    this.options = { ...MIC_ACTIVITY_DEFAULTS, ...options };
    this.reset();
  }

  reset() {
    this.noiseFloor = 0;
    this.initialised = false;
    this.loudSinceMs = 0;
    this.lastLoudAtMs = 0;
    this.confirmedUntilMs = 0;
    this.lastSampleAtMs = 0;
    this.turn = null;
  }

  /** Minimum level that counts as near-field speech right now. */
  threshold() {
    const { marginRatio, marginAbsolute, minLevel } = this.options;
    return Math.max(
      minLevel,
      this.noiseFloor * marginRatio,
      this.noiseFloor + marginAbsolute,
    );
  }

  isNearFieldConfirmed(nowMs = Date.now()) {
    return nowMs < this.confirmedUntilMs;
  }

  /**
   * False when the metering loop has stalled (backgrounded tab, throttled
   * animation frames). Callers must fail open rather than reject every turn.
   */
  hasFreshSamples(nowMs = Date.now()) {
    if (!this.lastSampleAtMs) return false;
    return nowMs - this.lastSampleAtMs <= this.options.freshSampleMs;
  }

  noteLevel(level, nowMs = Date.now()) {
    const value = Number.isFinite(level) && level > 0 ? level : 0;
    const previousSampleAtMs = this.lastSampleAtMs;
    this.lastSampleAtMs = nowMs;

    if (!this.initialised) {
      this.noiseFloor = value;
      this.initialised = true;
    } else {
      const rising = value > this.noiseFloor;
      // Freeze the estimate while near-field speech is confirmed, otherwise the
      // user's own voice drags the floor up and blinds the gate mid-sentence.
      if (!(rising && this.isNearFieldConfirmed(nowMs))) {
        const weight = rising ? this.options.floorRelease : this.options.floorAttack;
        this.noiseFloor += (value - this.noiseFloor) * weight;
      }
    }

    const threshold = this.threshold();
    const loud = value >= threshold;

    if (loud) {
      this.lastLoudAtMs = nowMs;
      if (!this.loudSinceMs) this.loudSinceMs = nowMs;
      if (nowMs - this.loudSinceMs >= this.options.sustainMs) {
        this.confirmedUntilMs = nowMs + this.options.holdMs;
      }
    } else if (this.loudSinceMs && nowMs - this.lastLoudAtMs > this.options.quietToleranceMs) {
      this.loudSinceMs = 0;
    }

    if (this.turn) {
      this.turn.peakLevel = Math.max(this.turn.peakLevel, value);
      this.turn.noiseFloor = Math.min(this.turn.noiseFloor, this.noiseFloor);
      this.turn.threshold = threshold;
      if (loud && previousSampleAtMs) {
        this.turn.loudMs += Math.min(nowMs - previousSampleAtMs, this.options.maxSampleGapMs);
      }
    }

    return loud;
  }

  /** Starts collecting loudness evidence for one user turn, discarding any stale one. */
  beginTurn(nowMs = Date.now()) {
    this.turn = {
      startedMs: nowMs,
      peakLevel: 0,
      loudMs: 0,
      noiseFloor: this.noiseFloor,
      threshold: this.threshold(),
    };
  }

  /** Ends the turn and returns its evidence, or null if none was collected. */
  finishTurn(nowMs = Date.now()) {
    const turn = this.turn;
    this.turn = null;
    if (!turn) return null;
    return {
      peakLevel: turn.peakLevel,
      loudMs: turn.loudMs,
      noiseFloor: turn.noiseFloor,
      threshold: turn.threshold,
      durationMs: Math.max(0, nowMs - turn.startedMs),
    };
  }
}
